import { Fragment } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { db, type Transaction } from '../db/database'
import { loadSummary, plannedTags } from '../db/budget'
import { formatAriary, formatSigned } from '../lib/money'
import { formatFullDate, monthOf, today } from '../lib/dates'
import { TransactionRow } from '../components/TransactionRow'
import { Icon } from '../components/Icon'
import { PendingConfirmations } from '../components/PendingConfirmations'
import { MonthMark } from '../components/MonthMark'
import { cycleLabel, cycleLabelSince, previousCycle } from '../lib/cycles'
import { navigate } from '../lib/router'
import './HomeScreen.css'

/**
 * Accueil : la boucle quotidienne de l'app.
 *
 * Un chiffre ne passe en gros que quand il appelle une action.
 *
 *   Début de cycle — il reste de l'argent à placer : c'est LUI le héros, avec
 *   le bouton qui mène à la répartition.
 *   En cours de cycle — tout est réparti : le héros devient ce qui est prévu
 *   aujourd'hui, à confirmer. Le solde global, lui, ne doit jamais dominer
 *   l'écran : c'est le chiffre qui fait dépenser trop, puisqu'il contient
 *   l'argent déjà réservé au loyer et à l'épargne.
 *
 * « En poche » reste néanmoins toujours affiché : c'est le seul montant de
 * l'app que l'utilisateur peut vérifier en recomptant ses billets.
 *
 * Toutes les lectures passent par useLiveQuery sur Dexie — aucun appel réseau.
 */
export function HomeScreen(
  { walletId, onOpen, onEdit, onDelete }: {
    walletId: string
    onOpen?: (t: Transaction) => void
    onEdit?: (t: Transaction) => void
    onDelete?: (t: Transaction, label: string) => void
  },
) {
  const data = useLiveQuery(async () => {
    const transactions = (await db.transactions.where({ wallet_id: walletId }).toArray())
      // `deleted_at` d'abord : une transaction supprimée qui compte dans un
      // solde, c'est un solde faux.
      .filter((t) => !t.deleted_at)
    const categories = await db.categories.where({ wallet_id: walletId }).toArray()
    const wallet = await db.wallets.get(walletId)
    return {
      transactions,
      categories,
      cycle: wallet?.cycle ?? 'monthly',
      // `?? null` et non `!== null` : un portefeuille écrit avant l'existence
      // du champ vaut `undefined`, pas `null`.
      expected: wallet?.expected_income ?? null,
      summary: await loadSummary(walletId),
      plans: await plannedTags(walletId),
    }
  }, [walletId])

  if (!data) return <div className="home-loading" />

  const { summary, cycle, expected } = data
  const incomeLabel = cycle === 'monthly' ? 'Revenus du mois' : 'Revenus de la semaine'
  const byId = new Map(data.categories.map((c) => [c.id, c]))

  const recent = [...data.transactions]
    .sort((a, b) => b.operation_date.localeCompare(a.operation_date) ||
                    // L'identifiant et non `client_updated_at` : ce dernier est
                    // réécrit à chaque modification, et corriger un montant
                    // faisait remonter la ligne en tête de sa journée. Les
                    // UUIDv7 portent leur date de création, qui ne bouge jamais.
                    b.id.localeCompare(a.id))
    .slice(0, 8)

  // Ce que la liste ne montre pas. Le compte, et non un simple booléen : « voir
  // les 12 autres » dit ce qu'on gagne à cliquer, « voir plus » laisse deviner.
  const reste = data.transactions.length - recent.length

  return (
    <div className="home">
      {/* Le repère temporel, en premier.
          Sans lui l'utilisateur ne sait ni quel jour on est, ni de quelle
          période il gère l'argent — et tout, dans cette app, dépend du cycle. */}
      <header className="home-head">
        <p className="home-today">{formatFullDate(today())}</p>
        <p className="home-cycle">Budget · {cycleLabel(summary.range, cycle)}</p>
      </header>

      {summary.toAllocate > 0 && (
        <section className="to-place" aria-labelledby="to-place-label">
          <p className="to-place-label" id="to-place-label">À placer</p>
          <output className="to-place-amount tabular">
            {formatAriary(summary.toAllocate)}
          </output>
          <button type="button" className="to-place-action" onClick={() => navigate('/budget')}>
            Répartir cet argent
          </button>
        </section>
      )}

      {/* Région nommée : un lecteur d'écran annonce l'intitulé avant le montant,
          au lieu de lire un nombre isolé. */}
      <section className="balance" aria-labelledby="balance-label">
        <p className="balance-label" id="balance-label">En poche</p>
        {/* <output> : c'est un résultat calculé, pas un paragraphe. */}
        <output className={`balance-amount tabular ${summary.inPocket < 0 ? 'is-negative' : ''}`}>
          {formatAriary(summary.inPocket)}
        </output>
        {/* D'où vient ce solde : ce qui est entré et ce qui est sorti SUR CE
            CYCLE. « En poche » traîne tout l'historique derrière lui — sans ces
            deux lignes, l'utilisateur ne retrouve pas son revenu du mois, qui
            est pourtant le repère à partir duquel il raisonne. */}
        <dl className="balance-flow">
          {/* Ce qui restait des cycles passés, et qui est toujours là.
              Sans cette ligne le compte ne tombait pas juste sous les yeux de
              l'utilisateur — « en poche 390 750 » alors que la ventilation
              annonce « +300 000 » et « 0 » se lit comme une erreur de l'app.
              Rien n'est écrit nulle part : c'est de l'argent déjà compté dans
              « en poche », l'inscrire en transaction le compterait deux fois.
              Masquée à zéro : au premier cycle, il n'y a rien à reporter. */}
          {summary.broughtForward !== 0 && (
            <div className="balance-flow-row">
              <dt>Reporté {cycleLabelSince(previousCycle(summary.range, cycle), cycle)}</dt>
              <dd className={`tabular ${
                summary.broughtForward < 0 ? 'is-expense' : 'is-income'}`}>
                {formatSigned(Math.abs(summary.broughtForward),
                              summary.broughtForward < 0 ? 'expense' : 'income')}
              </dd>
            </div>
          )}
          <div className="balance-flow-row">
            <dt>{incomeLabel}</dt>
            {/* Aucun revenu encore encaissé : on montre l'estimation, mais
                jamais du même ton ni du même vert qu'un montant réel — c'est un
                PLAN, et un plan affiché comme un encaissement fait croire à de
                l'argent qui n'existe pas (invariant 5). */}
            {summary.cycleIncome === 0 && expected ? (
              <dd className="is-expected tabular">attendu&nbsp;: {formatAriary(expected)}</dd>
            ) : (
              <dd className="is-income tabular">
                {formatSigned(summary.cycleIncome, 'income')}
              </dd>
            )}
          </div>
          <div className="balance-flow-row">
            <dt>Dépenses</dt>
            <dd className="is-expense tabular">
              {formatSigned(summary.cycleExpense, 'expense')}
            </dd>
          </div>
        </dl>

        <div className="balance-split">
          {summary.toAllocate === 0 ? (
            <span className="is-allocated">
              <Icon name="check" size={14} /> Tout est réparti
            </span>
          ) : (
            <span>dont <em>{formatAriary(Math.max(0, summary.inPocket - summary.toAllocate))}</em> déjà réservés</span>
          )}
        </div>
      </section>

      <PendingConfirmations walletId={walletId} scope="today" />

      <section>
        <header className="section-head">
          <h2>Dernières opérations</h2>
          {data.transactions.length > 0 && (
            <button type="button" onClick={() => navigate('/transactions')}>Tout voir</button>
          )}
        </header>

        {recent.length === 0 ? (
          <p className="empty">
            Aucune opération pour l'instant.<br />
            Touchez le bouton <strong>+</strong> pour enregistrer votre première dépense.
          </p>
        ) : (
          <>
            <ul className="rows">
              {recent.map((transaction, index) => (
                // Un fragment et non deux `map` : la marque de mois vit DANS la
                // liste, entre les lignes qu'elle sépare.
                <Fragment key={transaction.id}>
                  {(index === 0 || monthOf(transaction.operation_date)
                    !== monthOf(recent[index - 1].operation_date)) && (
                    <MonthMark date={transaction.operation_date} as="li" />
                  )}
                  <TransactionRow
                    transaction={transaction}
                    category={byId.get(transaction.category_id ?? '')}
                    plan={data.plans.get(transaction.id)}
                    showDate
                    onOpen={onOpen}
                    onEdit={onEdit}
                    onDelete={onDelete}
                  />
                </Fragment>
              ))}
            </ul>

            {/* Le même geste qu'en haut, mais là où le doigt se trouve : après
                huit lignes, remonter chercher « Tout voir » est un aller-retour
                que personne ne fait. */}
            {reste > 0 && (
              <button type="button" className="see-more"
                      onClick={() => navigate('/transactions')}>
                <Icon name="ellipsis" size={16} />
                Voir {reste === 1 ? "l'autre opération" : `les ${reste} autres opérations`}
              </button>
            )}
          </>
        )}
      </section>

    </div>
  )
}
